import React, { useEffect, useState } from 'react';
import { PasswordInput } from '../../../components/Form';
import { Text } from 'rebass';
import Button from '../../../components/Button/Button';
import { useHistory } from 'react-router-dom';
import { useParams } from 'react-router';
import AuthForm from '../components/AuthForm';
import { sendForgottenPasswordUpdate } from '../../../services/auth';

/**
 * @component for reset form
 */
const ResetPasswordForm: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordLengthFailed, setPasswordLengthFailed] = useState(false);
  const [confirmPasswordMatchFailed, setConfirmPasswordMatchFailed] =
    useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const history = useHistory();

  //gets token from param
  const { resetToken } = useParams<{
    resetToken: string;
  }>();

  // handles situation with no token
  if (!resetToken) {
    history.push('forgot-password');
  }

  // Handles when form is submitted
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage('');

    if (password && password !== confirmPassword) {
      setConfirmPasswordMatchFailed(true);
    } else if (confirmPasswordMatchFailed) {
      setConfirmPasswordMatchFailed(false);
    }

    if (password.length < 8) {
      setPasswordLengthFailed(true);
    } else if (passwordLengthFailed) {
      setPasswordLengthFailed(false);
    }

    const passwordPasses = password === confirmPassword && password.length >= 6;
    if (passwordPasses) {
      const payload = {
        resetToken,
        password,
      };

      if (!confirmPasswordMatchFailed) {
        setConfirmPasswordMatchFailed(false);
      }

      try {
        await sendForgottenPasswordUpdate(payload);
        setShowSuccessMessage(true);

        // redirect after successful password change
        setTimeout(() => {
          history.push('/login');
        }, 5000);
      } catch (error: any) {
        setErrorMessage(error.message);
      }
    }
  };

  // When password input changes
  const handlePasswordInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setErrorMessage('');
    setPassword(e.target.value);
  };

  // when confirm password input chaqnges
  const handleConfirmPasswordInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (password && confirmPasswordMatchFailed && e.target.value === password) {
      setConfirmPasswordMatchFailed(false);
    }
    setConfirmPassword(e.target.value);
  };

  return (
    <AuthForm onSubmit={handleSubmit}>
      {showSuccessMessage ? (
        <>
          <Text variant="heading3" mt={4}>
            Your password was successfully changed!
          </Text>
          <Text variant="body2" mt={4}>
            You will be redirected to the login page momentarily...
          </Text>
        </>
      ) : (
        <>
          <Text variant="heading2" mt={4}>
            Enter New Password
          </Text>
          <PasswordInput
            id="input-password"
            name="password"
            placeholder="New Password"
            mt={3}
            padding={5}
            value={password}
            onChange={handlePasswordInputChange}
            showPassword={showPassword}
            toggleShowPassword={() => setShowPassword(!showPassword)}
          />
          <PasswordInput
            id="input-confirm-password"
            name="confirm-password"
            placeholder="Confirm New Password"
            mt={3}
            padding={5}
            value={confirmPassword}
            onChange={handleConfirmPasswordInputChange}
            showPassword={showConfirmPassword}
            toggleShowPassword={() =>
              setShowConfirmPassword(!showConfirmPassword)
            }
          />

          <Text
            variant="subheading"
            mt={2}
            ml={1}
            sx={
              passwordLengthFailed ? { color: '#D9534F', fontSize: '13px' } : {}
            }
          >
            Password must be at least 8 characters long.
          </Text>
          {confirmPasswordMatchFailed && (
            <Text
              variant="subheading"
              mt={2}
              ml={1}
              sx={{ color: '#D9534F', fontSize: '13px' }}
            >
              Both passwords must match.
            </Text>
          )}
          {errorMessage && (
            <Text
              variant="subheading"
              mt={2}
              ml={1}
              sx={{ color: '#D9534F', fontSize: '13px' }}
            >
              {errorMessage}
            </Text>
          )}
          <Button mt={6} type="submit" variant="primary">
            Submit
          </Button>
        </>
      )}
    </AuthForm>
  );
};

export default ResetPasswordForm;
